<template>
  <div class="training-batch-setting-configure"
       v-loading="loading"
       element-loading-text="数据加载中，请稍等...">
    <template v-if="defaults">
      <div class="body">
        <div class="body-cont"
             v-loading="committing"
             element-loading-text="数据提交中，请稍等...">
          <AppScrollbar style="height: 100%">
            <div class="configure-main">
              <!-- 时间设置 -->
              <div class="config-item">
                <TitleGroup title="时间设置" :bold="false"/>
                <div class="value">
                  <el-form label-width="220px" size="mini">
                    <el-form-item label="选课申请表、实施计划书提交时间">
                      <el-date-picker v-model="form.topicDateRange"
                                      type="datetimerange"
                                      range-separator="至"
                                      start-placeholder="开始时间"
                                      end-placeholder="结束时间"
                                      value-format="yyyy-MM-dd HH:mm:ss"/>
                    </el-form-item>
                    <el-form-item label="项目成果提交时间">
                      <el-date-picker v-model="form.resultDateRange"
                                      type="datetimerange"
                                      range-separator="至"
                                      start-placeholder="开始时间"
                                      end-placeholder="结束时间"
                                      value-format="yyyy-MM-dd HH:mm:ss"/>
                    </el-form-item>
                  </el-form>
                </div>
              </div>
              <!-- 权重设置 -->
              <div class="config-item">
                <TitleGroup title="成绩权重设置" :bold="false"/>
                <div class="value">
                  <el-form label-width="120px" size="mini">
                    <el-form-item label="小组得分权重">
                      <el-input-number v-model="form.groupScoreWeight"
                                       :min="0" :max="100"
                                       @change="onGroupWeightChange"/>
                      <span class="unit">%</span>
                    </el-form-item>
                    <el-form-item label="个人得分权重">
                      <el-input-number v-model="form.personalScoreWeight"
                                       :min="0" :max="100"
                                       @change="onPersonalWeightChange"/>
                      <span class="unit">%</span>
                    </el-form-item>
                  </el-form>
                </div>
              </div>
              <!-- 其他设置 -->
              <div class="config-item">
                <TitleGroup title="其他设置" :bold="false"/>
                <div class="value">
                  <el-form label-width="120px" size="mini">
                    <el-form-item label="开启专家预审">
                      <el-switch v-model="form.expertVerifyOpen"
                                 active-value="1"
                                 inactive-value="0"/>
                    </el-form-item>
                  </el-form>
                </div>
              </div>
            </div>
          </AppScrollbar>
        </div>
      </div>
      <div class="footer">
        <el-button size="mini" type="primary"
                   :loading="committing"
                   @click="handleSave">保存设置</el-button>
      </div>
    </template>
    <template v-else>
      <Hint v-if="!loading" type="error">
        <div>配置信息加载失败</div>
        <el-button type="text" @click="loadDefaults">重新加载</el-button>
      </Hint>
    </template>
  </div>
</template>

<script>
import AppScrollbar from '../../../../../../../elements/AppScrollbar'
import TitleGroup from '../../../../../../../widgets/custom/TitleGroup'
import Hint from '../../../../../../../elements/Hint'

export default {
  name: 'TrainingBatchSettingConfigure',
  components: { AppScrollbar, TitleGroup, Hint },
  props: {
    batchId: { type: String, required: true }
  },
  data () {
    return {
      loading: false,
      committing: false,
      defaults: null,
      weightChanging: null,
      form: {
        topicDateRange: [],
        resultDateRange: [],
        groupScoreWeight: 50,
        personalScoreWeight: 50,
        expertVerifyOpen: '0'
      }
    }
  },
  watch: {
    'form.groupScoreWeight' (val, oldVal) {
      if (this.weightChanging === 'group') {
        this.form.personalScoreWeight = 100 - (val || 0)
      }
    },
    'form.personalScoreWeight' (val, oldVal) {
      if (this.weightChanging === 'personal') {
        this.form.groupScoreWeight = 100 - (val || 0)
      }
    }
  },
  methods: {
    onGroupWeightChange () {
      this.weightChanging = 'group'
      this.$nextTick(() => { this.weightChanging = null })
    },
    onPersonalWeightChange () {
      this.weightChanging = 'personal'
      this.$nextTick(() => { this.weightChanging = null })
    },
    loadDefaults () {
      this.loading = true
      this.$ajax({
        url: '/workspace/manager/training/batch/setting/defaults',
        method: 'post',
        data: this.$qs.stringify({ batchId: this.batchId })
      }).then(response => {
        if (response.data && response.data.responseCode === 'SUCCESS') {
          this.defaults = response.data.defaults || {}
          this.initForm()
        } else {
          this.$message.warning(response.data.message)
        }
      }, () => {
        this.$message.error('请求发送失败，请稍后重试')
      }).finally(() => {
        this.loading = false
      })
    },
    initForm () {
      const d = this.defaults
      this.form.topicDateRange = d.topicStartDate && d.topicEndDate
        ? [d.topicStartDate, d.topicEndDate] : []
      this.form.resultDateRange = d.resultStartDate && d.resultEndDate
        ? [d.resultStartDate, d.resultEndDate] : []
      this.form.groupScoreWeight = d.groupScoreWeight || 50
      this.form.personalScoreWeight = d.personalScoreWeight || 50
      this.form.expertVerifyOpen = d.expertVerifyOpen || '0'
    },
    handleSave () {
      if (this.form.groupScoreWeight + this.form.personalScoreWeight !== 100) {
        this.$message.warning('小组权重和个人权重之和必须为100%')
        return
      }
      this.committing = true
      const topicDateRange = this.form.topicDateRange || []
      const resultDateRange = this.form.resultDateRange || []
      const settingInfo = {
        topicStartDate: topicDateRange[0] || '',
        topicEndDate: topicDateRange[1] || '',
        resultStartDate: resultDateRange[0] || '',
        resultEndDate: resultDateRange[1] || '',
        groupScoreWeight: this.form.groupScoreWeight,
        personalScoreWeight: this.form.personalScoreWeight,
        expertVerifyOpen: this.form.expertVerifyOpen
      }
      this.$ajax({
        url: '/workspace/manager/training/batch/setting/submitDefaults',
        method: 'post',
        data: this.$qs.stringify({
          batchId: this.batchId,
          settingInfo: JSON.stringify(settingInfo)
        })
      }).then(response => {
        if (response.data && response.data.responseCode === 'SUCCESS') {
          this.$message.success('保存成功')
        } else {
          this.$message.warning(response.data.message)
        }
      }, () => {
        this.$message.error('请求发送失败，请稍后重试')
      }).finally(() => {
        this.committing = false
      })
    }
  },
  mounted () {
    this.loadDefaults()
  }
}
</script>

<style lang="scss">
.training-batch-setting-configure {
  height: 100%;
  display: flex;
  flex-direction: column;
  & > .body {
    flex: auto;
    position: relative;
    & > .body-cont {
      position: absolute;
      left: 0; top: 0; right: 0; bottom: 0;
      .configure-main {
        padding: 30px 15px;
        .config-item {
          max-width: 720px;
          margin: 0 auto;
          & + .config-item { margin-top: 30px; }
          .value { margin-top: 10px; }
          .unit { margin-left: 5px; color: #606266; }
        }
      }
    }
  }
  & > .footer {
    flex: none;
    height: 52px;
    border-top: 1px solid rgba(0,0,0,.05);
    text-align: center;
    line-height: 50px;
  }
}
</style>
